import { FC, useCallback, useState } from 'react';
import type { EvaluationRuleResult } from '@/entities/expert-report';
import { cn } from '@/shared/lib';
import { buildEvaluationTargetKey, EVALUATION_TARGET_HIGHLIGHT_CLASS, type EvaluationTarget } from '../model';
import { EvaluationCommentText } from './EvaluationCommentText';
import { EvaluationRuleCommentEditor } from './EvaluationRuleCommentEditor';
import { EvaluationRuleCommentHistory } from './EvaluationRuleCommentHistory';
import { RuleDetailDialog } from './RuleDetailDialog';

const RESULT_INDICATOR: Record<EvaluationRuleResult['result'], { symbol: string; className: string }> = {
  success: { symbol: '\u2713', className: 'text-success-foreground' },
  fail: { symbol: '\u2717', className: 'text-destructive' },
  partial: { symbol: '~', className: 'text-warning-foreground' },
  comment: { symbol: '📝', className: 'text-muted-foreground' },
};

type RuleRowProps = {
  rule: EvaluationRuleResult;
  conversationId: number;
  expertReportId: number;
  blockId: number;
  onRuleClick: (id: number) => void;
  evaluationTarget: EvaluationTarget;
};

const EvaluationRuleRow: FC<RuleRowProps> = ({
  rule,
  conversationId,
  expertReportId,
  blockId,
  onRuleClick,
  evaluationTarget,
}) => {
  const indicator = RESULT_INDICATOR[rule.result] ?? RESULT_INDICATOR.comment;
  const manualComment = rule.manualComment ?? null;
  const effectiveComment = manualComment ?? rule.comment;
  const isTarget = evaluationTarget.targetKey === buildEvaluationTargetKey('rule', rule.ruleId);
  return (
    <div
      ref={isTarget ? evaluationTarget.attachTarget : undefined}
      className={cn(
        // Поле под рамку выделения есть у каждого правила: рамка не рассекает подсветку добавленного
        // фрагмента и не выносит правило за ширину заключения, а строки от выделения не сдвигаются
        'flex flex-col gap-0.5 px-1 py-0.5',
        isTarget && `${EVALUATION_TARGET_HIGHLIGHT_CLASS} rounded-sm`,
      )}
    >
      <div className="flex items-baseline gap-1.5">
        <span className={indicator.className}>{indicator.symbol}</span>
        <span
          role="button"
          tabIndex={-1}
          className="text-primary underline cursor-pointer hover:text-primary/80"
          onClick={() => onRuleClick(rule.ruleId)}
        >
          {rule.ruleName || `#${rule.ruleId}`}
        </span>
        {/* Кнопки правила собраны у названия: их место не зависит от длины комментария и его содержимого */}
        {/* Правка доступна и без комментария: иначе правилу без комментария его не добавить, а сброс в пустой был бы необратим */}
        <EvaluationRuleCommentEditor
          conversationId={conversationId}
          expertReportId={expertReportId}
          ruleId={rule.ruleId}
          effectiveComment={effectiveComment}
          manualComment={manualComment}
        />
        {manualComment !== null && (
          <EvaluationRuleCommentHistory comment={rule.comment} expertReportId={expertReportId} blockId={blockId} />
        )}
      </div>
      {effectiveComment && (
        <div className="pl-5">
          <EvaluationCommentText
            comment={effectiveComment}
            expertReportId={expertReportId}
            blockId={blockId}
            previousComment={manualComment !== null ? rule.comment : undefined}
          />
        </div>
      )}
    </div>
  );
};

type Props = {
  rules: EvaluationRuleResult[];
  conversationId: number;
  expertReportId: number;
  blockId: number;
  /** Наведение на правленый элемент при переходе из «Обратной связи» */
  evaluationTarget: EvaluationTarget;
};

export const EvaluationRulesList: FC<Props> = ({
  rules,
  conversationId,
  expertReportId,
  blockId,
  evaluationTarget,
}) => {
  const [ruleDialogId, setRuleDialogId] = useState<number | undefined>();
  const handleRuleClick = useCallback((id: number) => setRuleDialogId(id), []);
  return (
    <>
      <div className="flex flex-col gap-1">
        {rules.map((rule, index) => (
          <EvaluationRuleRow
            key={index}
            rule={rule}
            conversationId={conversationId}
            expertReportId={expertReportId}
            blockId={blockId}
            onRuleClick={handleRuleClick}
            evaluationTarget={evaluationTarget}
          />
        ))}
      </div>
      <RuleDetailDialog
        ruleId={ruleDialogId}
        expertReportId={expertReportId}
        blockId={blockId}
        onClose={() => setRuleDialogId(undefined)}
      />
    </>
  );
};
